<template>
    <Modal
        :isOpen="isOpen"
        @close="$emit('close')"
        title="Create new trip for redemption"
        width="720px"
        :isTeleported="true"
    >
        <template #subtitle>
            <p class="text-slate-500 dark:text-slate-400 text-sm leading-5 font-normal mt-1">
                {{ redemptionSubtitle }}
            </p>
        </template>
        <template #content>
            <Input
                label="Name"
                name="title"
                id="redemption-trip-name"
                placeholder="Name your trip"
                v-model:inputValue="form.title"
                class="mb-6"
                :errors="formErrors"
                @onFocus="clearError('title')"
            />
            <Input
                id="redemption-trip-destination"
                name="country"
                label="Destination"
                placeholder="Destination"
                :errors="formErrors"
                class="mb-6"
            >
                <template #input>
                    <div class="flex items-center gap-2">
                        <div class="relative grow">
                            <Suspense>
                                <PlacesAutocomplete
                                    :types="['(regions)']"
                                    id="redemption-trip-destination"
                                    placeholder="Destination"
                                    @placeChanged="placeChanged"
                                    @placeCleared="placeCleared"
                                    @onFocus="clearError('country')"
                                    :error="!!formErrors.country"
                                    :currentPlace="currentPlace"
                                    :class="{
                                        '!border-red-500': formErrors.country,
                                    }"
                                />
                            </Suspense>
                        </div>
                        <button
                            v-if="additionalDestinations.length > 0"
                            type="button"
                            class="shrink-0 p-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 focus:outline-none transition-colors duration-150"
                            @click="removeMainDestination"
                        >
                            <Icon icon="close" :width="16" :height="16" />
                        </button>
                    </div>
                </template>
                <template #error>
                    <div
                        class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                        role="alert"
                        v-if="formErrors.country"
                    >
                        <div class="flex items-center justify-center">
                            <Icon icon="caution" />
                        </div>
                        {{ formErrors.country }}
                    </div>
                </template>
            </Input>

            <div class="mb-6" v-if="additionalDestinations.length">
                <div
                    v-for="(destination, index) in additionalDestinations"
                    :key="index"
                    class="mb-3 flex items-center gap-2"
                >
                    <div class="relative grow">
                        <Suspense>
                            <PlacesAutocomplete
                                :types="['(regions)']"
                                placeholder="Select additional destination"
                                class="w-full"
                                :currentPlace="destination"
                                @placeChanged="(place) => additionalPlaceChanged(place, index)"
                                @placeCleared="() => additionalPlaceCleared(index)"
                            />
                        </Suspense>
                    </div>
                    <button
                        type="button"
                        class="shrink-0 p-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 focus:outline-none transition-colors duration-150"
                        @click="removeDestination(index)"
                    >
                        <Icon icon="close" :width="16" :height="16" />
                    </button>
                </div>
            </div>

            <div class="mb-6 flex justify-start">
                <button
                    type="button"
                    class="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-100 font-medium hover:text-orange-500 transition-colors duration-200"
                    @click="addDestination"
                >
                    <Icon icon="plus" :width="16" :height="16" />
                    Add destination
                </button>
            </div>

            <div class="w-full flex gap-4 mb-6">
                <Input
                    id="redemption-trip-start"
                    name="start_date"
                    label="Start"
                    placeholder="Start"
                    :options="{
                        icon: 'calendar',
                        iconPosition: 'right',
                        wrapperClass: 'flex-1',
                    }"
                    :errors="formErrors"
                >
                    <template v-slot:input>
                        <FlatPickr
                            :config="dateStartConfig"
                            v-model="form.start_date"
                            name="start_date"
                            id="redemption-trip-start-date"
                            class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pr-10 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="Start"
                            :class="{
                                '!border-red-500': formErrors.start_date,
                            }"
                            @on-change="onStartChange"
                            @focus="clearError('start_date')"
                        />
                    </template>
                </Input>
                <Input
                    id="redemption-trip-end"
                    name="end_date"
                    label="End"
                    placeholder="End"
                    :options="{
                        icon: 'calendar',
                        iconPosition: 'right',
                        wrapperClass: 'flex-1',
                    }"
                    :errors="formErrors"
                >
                    <template v-slot:input>
                        <FlatPickr
                            :config="dateEndConfig"
                            v-model="form.end_date"
                            name="end_date"
                            id="redemption-trip-end-date"
                            class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pr-10 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="End"
                            :class="{
                                '!border-red-500': formErrors.end_date,
                            }"
                            @on-change="onEndChange"
                            @focus="clearError('end_date')"
                        />
                    </template>
                </Input>
            </div>
            <Textarea
                label="Description"
                name="description"
                id="redemption-trip-description"
                class="mb-6"
                placeholder="Add any notes"
                v-model:inputValue="form.description"
                :errors="formErrors"
                :options="{
                    optional: true,
                }"
                @onFocus="clearError('description')"
            />
            <Input
                label="Cover"
                name="cover"
                id="redemption-trip-cover"
                placeholder="Upload or select cover image"
                class="mb-6"
                :errors="formErrors"
                :options="{
                    optional: true,
                }"
            >
                <template #input>
                    <TripImageUpload
                        v-bind:query="destinationSearchQuery"
                        :image="form.image"
                        :trip="{ id: null }"
                        @save="updateCoverImage"
                    />
                </template>
            </Input>
        </template>
        <template #footer>
            <div class="flex justify-end items-center">
                <div class="flex gap-4">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="$emit('close')"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="submitting"
                        @click="handleSubmit"
                    >
                        Create trip & move redemption
                        <span class="inline ml-2" v-if="submitting">
                            <i class="fas fa-spinner-third fa-spin"></i>
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { notify } from 'notiwind';
import Modal from '@/components/ui/modal/ui-modal.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import Textarea from '@/components/ui/inputs/ui-textarea.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import PlacesAutocomplete from '@/components/ui/places-autocomplete/places-autocomplete.vue';
import TripImageUpload from '@/components/trips/trip-image-upload.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useTripsStore } from '@/store/trips';
import { Apii } from '@/services/api';

const ApiService = new Apii();

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    redemption: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['close', 'created']);

const submitting = ref(false);
const formErrors = reactive({});
const hasSelectedValidPlace = ref(false);
const additionalDestinations = ref([]);
const MAX_DESTINATIONS = 20;
const DESTINATION_REQUIRED_MESSAGE = 'Please choose a destination from the suggestions';

const form = reactive({
    title: null,
    city: null,
    state: null,
    country: null,
    country_short: null,
    latitude: null,
    longitude: null,
    start_date: null,
    end_date: null,
    description: null,
    image: null,
    destinations: null,
});

const dateStartConfig = ref({
    dateFormat: 'm/d/Y',
    disableMobile: true,
});

const dateEndConfig = ref({
    dateFormat: 'm/d/Y',
    disableMobile: true,
});

const redemptionSubtitle = computed(() => {
    if (!props.redemption) return '';
    const parts = [];
    const name = getRedemptionDisplayName(props.redemption);
    if (name) parts.push(name);
    const startDate = props.redemption.start_date;
    if (startDate) {
        const formatted = formatDateShort(startDate);
        const endDate = props.redemption.end_date;
        if (endDate) {
            parts.push(`${formatted} – ${formatDateShort(endDate)}`);
        } else {
            parts.push(formatted);
        }
    }
    return parts.join(', ');
});

const currentPlace = computed(() => {
    if (!form.city && !form.country) return null;
    const city = form.city || '';
    let state = form.state || '';
    if (state) state = ' ' + state;
    const country = form.country_short || form.country || '';

    let formattedAddress = '';
    if ((state || city) && country) {
        formattedAddress = `${city}${state}, ${country}`;
    } else if (state || city) {
        formattedAddress = state || city;
    } else {
        formattedAddress = form.country;
    }

    return {
        formattedAddress,
        city,
        state,
        country,
        valueForSearch: destinationSearchQuery.value,
    };
});

const destinationSearchQuery = computed(() => {
    const city = (form.city || '').trim();
    const state = (form.state || '').trim();
    const country = (form.country || '').trim();
    return `${city}+${state}+${country}`
        .replace(/,/g, '')
        .replace(/ /g, '+')
        .replace(/^\+/, '')
        .replace(/\+$/, '')
        .replace(/\+\+/, '+')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
});

const getRedemptionDisplayName = (redemption) => {
    if (!redemption) return '';
    const type = redemption.redemption_type?.slug;
    if (type === 'flight' && redemption.travel_segments?.length > 0) {
        const segments = redemption.travel_segments;
        const origin = segments[0]?.destination_name || segments[0]?.origin || '';
        const destination =
            segments[segments.length - 1]?.destination_name ||
            segments[segments.length - 1]?.destination || '';
        return `${origin} – ${destination}`;
    }
    return redemption.name || '';
};

const formatDateShort = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatDateForInput = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
};

const prefillFromRedemption = () => {
    if (!props.redemption) return;

    const r = props.redemption;

    // Pre-fill dates
    form.start_date = formatDateForInput(r.start_date);
    form.end_date = formatDateForInput(r.end_date);

    // Pre-fill destination from redemption data
    const type = r.redemption_type?.slug;

    // Title is always blank — user must type it manually
    form.title = null;

    // Pre-fill destination from redemption data
    if (type === 'hotel' && r.hotel) {
        // Hotels store the city and state as locality and region.
        if (r.hotel.locality) form.city = r.hotel.locality;
        if (r.hotel.region) form.state = r.hotel.region;
        if (r.hotel.country) form.country = r.hotel.country;

        // A trip needs coordinates; without them the create request failed
        // with a 500 even though the destination looked filled in (SUPPORT-835).
        // The hotel catalog stores unknown coordinates as 0,0, so those don't count.
        const latitude = Number(r.hotel.latitude);
        const longitude = Number(r.hotel.longitude);
        const hasCoordinates = r.hotel.latitude != null && r.hotel.longitude != null
            && !(latitude === 0 && longitude === 0);

        if (hasCoordinates) {
            form.latitude = latitude;
            form.longitude = longitude;
            hasSelectedValidPlace.value = true;
        }
    }
};

const resetForm = () => {
    form.title = null;
    form.city = null;
    form.state = null;
    form.country = null;
    form.country_short = null;
    form.latitude = null;
    form.longitude = null;
    form.start_date = null;
    form.end_date = null;
    form.description = null;
    form.image = null;
    form.destinations = null;
    Object.keys(formErrors).forEach((key) => delete formErrors[key]);
    hasSelectedValidPlace.value = false;
    additionalDestinations.value = [];
    dateStartConfig.value.maxDate = null;
    dateEndConfig.value.minDate = null;
};

const clearError = (field) => {
    if (formErrors[field]) {
        delete formErrors[field];
    }
};

const placeChanged = (data) => {
    if (data) {
        let city = '';
        let state = '';
        let country = '';

        if (data.addressTypes) {
            if (data.addressTypes.locality) {
                city = data.addressTypes.locality.longName || data.addressTypes.locality.shortName || '';
            }
            if (data.addressTypes.administrative_area_level_1) {
                state = data.addressTypes.administrative_area_level_1.longName ||
                    data.addressTypes.administrative_area_level_1.shortName || '';
            }
            if (data.addressTypes.country) {
                country = data.addressTypes.country.longName || data.addressTypes.country.shortName || '';
            }
        } else {
            city = data.city || '';
            state = data.state || '';
            country = data.country || '';
        }

        form.city = city;
        form.state = state;
        form.country = country;
        form.latitude = data.lat ?? data.latitude ?? null;
        form.longitude = data.lng ?? data.longitude ?? null;
    }
    hasSelectedValidPlace.value = true;
};

const placeCleared = () => {
    form.city = null;
    form.state = null;
    form.country = null;
    form.latitude = null;
    form.longitude = null;
    hasSelectedValidPlace.value = false;
};

const addDestination = () => {
    if (additionalDestinations.value.length + 1 >= MAX_DESTINATIONS) {
        notify({
            group: 'error',
            title: 'Maximum destinations reached',
            text: `You can only add up to ${MAX_DESTINATIONS} destinations per trip`,
        });
        return;
    }
    additionalDestinations.value.push(null);
};

const removeDestination = (index) => {
    additionalDestinations.value.splice(index, 1);
};

const additionalPlaceChanged = (place, index) => {
    additionalDestinations.value[index] = place;
};

const additionalPlaceCleared = (index) => {
    additionalDestinations.value[index] = null;
};

const removeMainDestination = () => {
    placeCleared();
    additionalDestinations.value = [];
};

const onStartChange = (selectedDates, dateStr) => {
    dateEndConfig.value.minDate = dateStr;
};

const onEndChange = (selectedDates, dateStr) => {
    dateStartConfig.value.maxDate = dateStr;
};

const updateCoverImage = (imageUrl) => {
    form.image = imageUrl;
};

const validate = () => {
    Object.keys(formErrors).forEach((key) => delete formErrors[key]);

    if (!form.title) {
        formErrors.title = 'Please enter a trip name';
    }
    if (!form.start_date) {
        formErrors.start_date = 'Please enter a start date';
    }
    if (!form.end_date) {
        formErrors.end_date = 'Please enter an end date';
    }
    if (form.latitude == null || form.longitude == null) {
        formErrors.country = DESTINATION_REQUIRED_MESSAGE;
    }

    return Object.keys(formErrors).length === 0;
};

const compileDestinations = () => {
    const structuredDestinations = [];

    if (form.city || form.country) {
        const city = (form.city || '').trim();
        const state = (form.state || '').trim();
        const country = (form.country || '').trim();

        let formattedAddress = '';
        if (city && country) {
            formattedAddress = state ? `${city}, ${state}, ${country}` : `${city}, ${country}`;
        } else if (city) {
            formattedAddress = state ? `${city}, ${state}` : city;
        } else if (country) {
            formattedAddress = country;
        }

        structuredDestinations.push({
            city,
            state,
            country,
            latitude: form.latitude ?? null,
            longitude: form.longitude ?? null,
            formatted_address: formattedAddress,
        });
    }

    for (const dest of additionalDestinations.value) {
        if (dest?.formattedAddress) {
            let city = '';
            let state = '';
            let country = '';

            if (dest.addressTypes) {
                if (dest.addressTypes.locality) {
                    city = dest.addressTypes.locality.longName || dest.addressTypes.locality.shortName || '';
                }
                if (dest.addressTypes.administrative_area_level_1) {
                    state = dest.addressTypes.administrative_area_level_1.longName ||
                        dest.addressTypes.administrative_area_level_1.shortName || '';
                }
                if (dest.addressTypes.country) {
                    country = dest.addressTypes.country.longName || dest.addressTypes.country.shortName || '';
                }
            }

            structuredDestinations.push({
                city: city.trim(),
                state: state.trim(),
                country: country.trim(),
                latitude: dest.lat ?? null,
                longitude: dest.lng ?? null,
                formatted_address: dest.formattedAddress.trim(),
            });
        }
    }

    if (structuredDestinations.length > 0) {
        form.destinations = structuredDestinations;
    }
};

const handleSubmit = async () => {
    if (!validate()) return;
    if (!props.redemption) return;

    compileDestinations();
    submitting.value = true;

    try {
        const response = await ApiService.apiCall(
            `/redemptions/${props.redemption.id}/create-trip-and-attach`,
            'post',
            {
                title: form.title,
                image: form.image,
                start_date: form.start_date,
                end_date: form.end_date,
                description: form.description,
                city: form.city,
                state: form.state,
                country: form.country,
                country_short: form.country_short,
                latitude: form.latitude,
                longitude: form.longitude,
                destinations: form.destinations,
            }
        );

        const data = response.data || response;
        const trip = data.trip;
        const redemption = data.redemption || props.redemption;

        // Refresh trips in store
        const tripsStore = useTripsStore();
        tripsStore.getTrips();

        emit('created', { trip, redemption });
        emit('close');

        // Show success notification with link
        notify(
            {
                group: 'success',
                title: 'Redemption moved to trip',
                text: `Redemption was successfully moved to new trip "${trip.title}"`,
                url: `/trips/${trip.uuid}?redemption=${redemption.id}`,
                urlLabel: 'View redemption',
            },
            8000
        );
    } catch (err) {
        console.error('Failed to create trip and attach redemption:', err);
        const errors = err?.response?.data?.errors;
        if (errors) {
            Object.assign(formErrors, errors);
            // The form shows destination problems under the country field.
            if (errors.latitude || errors.longitude) {
                formErrors.country = DESTINATION_REQUIRED_MESSAGE;
            }
        } else {
            notify({
                group: 'error',
                title: 'Error',
                text: 'Failed to create trip. Please try again.',
            });
        }
    } finally {
        submitting.value = false;
    }
};

watch(
    () => props.isOpen,
    (newVal) => {
        if (newVal) {
            resetForm();
            prefillFromRedemption();
        }
    }
);
</script>
